import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FieldInput, clearOptionsCache } from "./FieldInput";
import { api, errMsg, fmtMoney, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const VISIBLE = {
  commissions: {
    revendeur_id: (f) => f.beneficiaire_type === "revendeur",
    superviseur_id: (f) => f.beneficiaire_type === "superviseur",
    compte_id: (f) => f.mode === "paiement",
  },
  dettes: {
    revendeur_id: (f) => f.tiers_type === "revendeur",
    superviseur_id: (f) => f.tiers_type === "superviseur",
    tiers_nom: (f) => ["fournisseur", "autre"].includes(f.tiers_type),
  },
  operations: {
    revendeur_id: (f) => f.personne_type === "revendeur",
    superviseur_id: (f) => f.personne_type === "superviseur",
    tpe_id: (f) => f.personne_type === "tpe",
    personne_nom: (f) => f.personne_type === "autre",
  },
  stock_mouvements: {
    lieu_type: (f) => LIEU_T.includes(f.type),
    fournisseur: (f) => f.type === "entree",
    depot_id: (f) => ["entree", "distribution", "retour_superviseur"].includes(f.type) || atLieu(f, "depot"),
    superviseur_id: (f) => ["distribution", "affectation", "retour_revendeur", "retour_superviseur"].includes(f.type) || atLieu(f, "superviseur"),
    revendeur_id: (f) => ["affectation", "retour_revendeur"].includes(f.type) || atLieu(f, "revendeur"),
    autoriser_negatif: (f) => f.type && !["entree", "ajustement_plus"].includes(f.type),
    motif_negatif: (f) => !!f.autoriser_negatif,
  },
  stock_inventaires: {
    depot_id: (f) => f.lieu_type === "depot",
    superviseur_id: (f) => f.lieu_type === "superviseur",
    revendeur_id: (f) => f.lieu_type === "revendeur",
  },
};
const LIEU_T = ["perte", "ajustement_plus", "ajustement_moins"];
function atLieu(f, t) {
  return LIEU_T.includes(f.type) && f.lieu_type === t;
}

function TfjPreview({ form }) {
  const [taux, setTaux] = useState(null);
  useEffect(() => {
    if (!form.revendeur_id) return;
    api.get(`/r/revendeurs/${form.revendeur_id}`).then((r) => setTaux(r.data.taux_commission ?? 0)).catch(() => {});
  }, [form.revendeur_id]);
  if (!form.revendeur_id) return null;
  const ventes = Number(form.ventes || 0);
  const commission = Math.round((ventes * Number(taux || 0)) / 100);
  const net = ventes - Number(form.gains_payes || 0) - commission;
  const verse = form.montant_verse === "" || form.montant_verse == null ? net : Number(form.montant_verse);
  const rows = [["Taux commission", `${taux ?? "…"} %`], ["Commission", fmtMoney(commission)],
    ["Net à verser", fmtMoney(net)], ["Écart", fmtMoney(net - verse)]];
  return (
    <div className="col-span-full grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 rounded-md bg-accent/60 border border-accent" data-testid="tfj-preview">
      {rows.map(([l, v]) => (
        <div key={l}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          <div className="font-mono text-sm font-semibold">{v}</div>
        </div>
      ))}
      <p className="col-span-full text-[11px] text-muted-foreground">Net à verser = Ventes − Gains payés − Commission revendeur (aperçu, recalculé par le serveur).</p>
    </div>
  );
}

function VirementPreview({ form }) {
  const { meta } = useAuth();
  const [c, setC] = useState({});
  useEffect(() => {
    if (!form.compte_source_id || !form.compte_dest_id) return;
    Promise.all([api.get(`/r/comptes/${form.compte_source_id}`), api.get(`/r/comptes/${form.compte_dest_id}`)])
      .then(([s, d]) => setC({ s: s.data, d: d.data })).catch(() => {});
  }, [form.compte_source_id, form.compte_dest_id]);
  if (!c.s || !c.d) return null;
  const montant = Number(form.montant || 0);
  const taux = c.s.frais_transfert_banque && c.d.type === "banque" ? Number(meta.settings?.taux_frais_mobile_money || 0) : 0;
  const frais = Math.round((montant * taux) / 100);
  const rows = [["Montant transféré", fmtMoney(montant)], [`Frais financiers ${taux} %`, fmtMoney(frais)], ["Net crédité", fmtMoney(montant - frais)]];
  return (
    <div className="col-span-full grid grid-cols-3 gap-2 p-3 rounded-md bg-accent/60 border border-accent" data-testid="virement-preview">
      {rows.map(([l, v]) => (
        <div key={l}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          <div className="font-mono text-sm font-semibold">{v}</div>
        </div>
      ))}
      {taux > 0 && <p className="col-span-full text-[11px] text-muted-foreground">Frais enregistrés automatiquement comme charge financière à la validation du transfert.</p>}
    </div>
  );
}

export function ResourceForm({ res, cfg, open, onOpenChange, item, fixedValues = {}, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const editable = cfg.fields.filter((f) => !f.readonly && !f.computed && !(f.name in fixedValues));

  useEffect(() => {
    if (!open) return;
    const init = {};
    cfg.fields.forEach((f) => {
      if (f.readonly || f.computed) return;
      init[f.name] = item ? item[f.name] ?? (f.type === "multirefitem" ? [] : "") : f.default ?? (f.type === "date" && f.name === "date" ? today() : f.type === "multirefitem" ? [] : "");
    });
    setForm({ ...init, ...fixedValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (res === "tfj" && payload.montant_verse === "") payload.montant_verse = null;
      if (item) await api.put(`/r/${res}/${item.id}`, payload);
      else await api.post(`/r/${res}`, payload);
      clearOptionsCache();
      toast.success(item ? "Modification enregistrée" : "Enregistrement créé");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const vis = VISIBLE[res] || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${res}-form-dialog`}>
        <DialogHeader>
          <DialogTitle>{item ? `Modifier ${cfg.singular}` : `Nouveau ${cfg.singular}`}</DialogTitle>
          <DialogDescription>
            {cfg.workflow ? "Enregistré en brouillon. La validation figera l'opération et générera les écritures." : "Les champs marqués * sont obligatoires."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editable.filter((f) => !vis[f.name] || vis[f.name](form)).map((f) => (
            <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-semibold">
                {f.label}{f.required && <span className="text-primary"> *</span>}
              </Label>
              <div className="mt-1">
                <FieldInput field={f} form={form} value={form[f.name]} testId={`field-${f.name}`}
                  onChange={(v) => setForm((s) => ({ ...s, [f.name]: v, ...(f.name === "revendeur_id" ? { tpe_id: s.tpe_id } : {}) }))} />
              </div>
              {f.help && <p className="text-[11px] text-muted-foreground mt-1">{f.help}</p>}
            </div>
          ))}
          {res === "tfj" && <TfjPreview form={form} />}
          {res === "virements" && <VirementPreview form={form} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-button">Fermer</Button>
          <Button onClick={save} disabled={saving} data-testid="form-submit-button">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
